import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, CheckSquare, Users, LogOut, Kanban, Calendar as CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { UserStatus } from './UserStatus';

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const allNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/', roles: null },
    { icon: Kanban, label: 'Kanban', path: '/kanban', roles: null },
    { icon: Users, label: 'Clients', path: '/clients', roles: ['admin', 'agency_manager'] },
    { icon: CalendarDays, label: 'Calendar', path: '/calendar', roles: ['account_manager', 'admin', 'agency_manager'] },
    { icon: CalendarDays, label: 'Approvals', path: '/approvals', roles: ['admin', 'agency_manager'] },
    { icon: Kanban, label: 'Team Kanban', path: '/team-kanban', roles: ['admin', 'manager', 'agency_manager'] },
    { icon: CheckSquare, label: 'Tasks', path: '/tasks', roles: null },
    { icon: Users, label: 'Users', path: '/users', roles: ['admin'] },
  ];

  const navItems = allNavItems.filter(item => !item.roles || item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="w-64 glassmorphic-sidebar flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-slate-200/60">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center p-1">
            <img
              src="/bbc-logo.png"
              alt="BBC Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold font-outfit text-slate-900">BBC Task Engine</h1>
            <p className="text-xs text-slate-500">{user?.role?.toUpperCase().replace('_', ' ')}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              onClick={() => navigate(item.path)}
              variant="ghost"
              className={`w-full justify-start h-12 rounded-xl font-medium transition-colors ${
                isActive
                  ? 'bg-[#2D3F63]/10 text-[#2D3F63] hover:bg-[#2D3F63]/10'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <Icon className="w-5 h-5 mr-3" />
              {item.label}
            </Button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-200/60">
        <div className="flex items-center justify-between space-x-3 p-3 mb-3 bg-slate-50 rounded-xl">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2D3F63] to-[#4A5F8C] flex items-center justify-center text-white font-semibold relative">
              {user?.full_name?.charAt(0)?.toUpperCase()}
              <div className="absolute -bottom-1 -right-1">
                {user && <UserStatus user={user} showLabel={false} size="sm" />}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="ghost"
          className="w-full justify-start text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-xl h-11"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
      </div>
    </aside>
  );
};